import React, { useState, useEffect, useRef, useContext, useImperativeHandle, forwardRef, use } from 'react';
import TypedLink from '../TypedLink/TypedLink.js';
import SlideInContent from '../AnimatedElements/SlideInContent.js';
import { AppContext, ModalsContext, withAppContext, useAppContext } from '../../contexts/AppContext';
import './ProductsMenu.css';
import HorisontalCategoryPicker from '../HorisontalCategoryPicker/HorisontalCategoryPicker.js';
import Minicart from '../Minicart/Minicart.js';
import Search from '../Search/Search.js';


const ProductsMenu = forwardRef((props, ref) => {
  const modalsContext = useContext(ModalsContext);
  const appContext = useAppContext();
  const appRef = appContext.appRef;
  const categoriesOrder = appRef.current.state.categories?.order_by || [];
  
  const [show, setShow] = useState(false);
  const [slideDirection, setSlideDirection] = useState('');
  const [activeKey, setActiveKey] = useState(null);
  const [useAnimation, setUseAnimation] = useState(true);
  const [dataToRender, setDataToRender] = useState({});
  const [secoundLevelSelectedKey, setSecoundLevelSelectedKey] = useState(null);
  const [thirdLevelDataToRender, setThirdLevelDataToRender] = useState({});

  const animationObjectRef = useRef(null);
  const pendingHideRef = useRef(false);

  const handleAnimationEnd = () => {
    setSlideDirection('');
  };

  useEffect(() => {
    const getCategoryDataByUrlHash = () => {
      const hashValue = window.location.hash.replace(/^#/, '') || 'home';
      
    };

    const handleSlideDirection = (e) => {
      setSlideDirection(e.detail);
    };

    getCategoryDataByUrlHash();
    window.addEventListener('hashchange', getCategoryDataByUrlHash, false);
    window.addEventListener('categorySlideDirection', handleSlideDirection, false);

    return () => {
      window.removeEventListener('hashchange', getCategoryDataByUrlHash, false);
      window.removeEventListener('categorySlideDirection', handleSlideDirection, false);
    };
  }, []);

  const setActiveKeyLogic = (key) => {

    let set_key = key;

    if(key==='home'){
      const currentCategoriesOrder = appRef.current.state.categories?.order_by || [];
      set_key = currentCategoriesOrder.length > 0 ? currentCategoriesOrder[0] : null;

    }

    const newData = appRef.current.state.categories?.categories[set_key] || {};

    setDataToRender(newData);

    if(newData && newData.sublevels && Object.keys(newData.sublevels).length>0 && newData.order_by.length>0){
      const firstSublevelKey = newData.order_by[0];
      const thirdLevelData = newData.sublevels[firstSublevelKey];
      setThirdLevelDataToRender(thirdLevelData);
      setSecoundLevelSelectedKey(firstSublevelKey);
    }

    setActiveKey(set_key);
  }

  useEffect(() => {
    setActiveKeyLogic(appRef.current.state.category_picker_active_key);
    if (show) {
      setUseAnimation(false);
    }
  }, [appRef.current.state.category_picker_active_key,appRef.current.state.categories?.order_by]);

  const is_visible = () => show;

  const showMenu = () => {
    if (show) {
      hideMenu();
      return;
    }
    setUseAnimation(true); 
    setShow(true);
    setTimeout(() => animationObjectRef.current?.setVisible(true), 0);
    appRef.current.setProductMenuOpened(true);
    appRef.current.set_scrolled(false);
  };

  const hideMenu = () => {
    if (!show) {
      pendingHideRef.current = false;
      appRef.current.setProductMenuOpened(false);
      modalsContext.AppFooterMenuRef?.current?.set_item_inactive_by_name('products_menu');
      return;
    }

    pendingHideRef.current = true;
    appRef.current.setProductMenuOpened(false);
    animationObjectRef.current?.setVisible(false);
  };

  const toggle = () => {
    if (show) {
      hideMenu();
    } else {
      showMenu();
    }
  };

  useImperativeHandle(ref, () => ({
    show: showMenu,
    hide: hideMenu,
    toggle: toggle,
    is_visible: is_visible,
  }));

  const handleSlideOutComplete = () => {
    if (pendingHideRef.current) {
      pendingHideRef.current = false;
      setShow(false);
      modalsContext.AppFooterMenuRef?.current?.set_item_inactive_by_name('products_menu');
    }
  };

  const renderContent = () => {
    return (
      <div className={`floating_container_inner`}>
        <div className='floating_container_header'>
          <div className='floating_top'>
            <Search />
            <Minicart />
          </div>
          <HorisontalCategoryPicker  flag="products_menu" />
        </div>
        <div className='floating_container_content'>
          {render_categories()}
        </div>
      </div>
    );
  };

  const render_categories = () => {
    
    if (!dataToRender || typeof dataToRender !== 'object' || Array.isArray(dataToRender) || !dataToRender.order_by) {
      return null;
    }
    
    const slideClass = slideDirection ? ' slide-' + slideDirection : '';

    return <div className={`products_menu_categories_container${slideClass}`} onAnimationEnd={handleAnimationEnd}>
      <div className='products_menu_categories_inner'>
        <div className='products_menu_secound_level'>
          {dataToRender.order_by.map((key) => {
            const category = dataToRender.sublevels[key];
            const isSelected = (secoundLevelSelectedKey === key);
            if (!category) return null;
            return <div key={'sublevel_'+key} className={'products_menu_category_item'+(isSelected ? ' active' : '')} onClick={()=>{
              setSecoundLevelSelectedKey(key);
              setThirdLevelDataToRender(category);
            }}>
              {Object.keys(category.sublevels || {}).length>0 
              ? <span className='category_has_sublevels'>{category.category}</span> 
              : <TypedLink onClick={hideMenu} link={{type: 'products', url: dataToRender.url+'/'+category.url}}><span>{category.category}</span></TypedLink>}
            </div>
          })}
        </div>
        <div className='products_menu_third_level'>
          {thirdLevelDataToRender.order_by && thirdLevelDataToRender.order_by.map((key) => {

            const category = thirdLevelDataToRender.sublevels[key];
            const subimage = (category.app_category_image) ? process.env.REACT_APP_API_SERVER_URL+'upload_files/categories/'+category.app_category_image : '/assets/images/no_image_category.png';
            const has_image = (category.app_category_image) ? true : false;

            if (!category) return null; 

            return <div key={'thirdlevel_'+key} className='products_menu_category_item'>
              <TypedLink onClick={hideMenu} link={{type: 'products', url: dataToRender.url+'/'+thirdLevelDataToRender.url+'/'+category.url}}>
                <span className='third_level_image'><img src={subimage} alt={category.category}/></span> 
                <span>{category.category}</span>
              </TypedLink>
            </div>
          })}
        </div>
      </div>
    </div>;

  };

  if (!show) {
    return null; 
  }

  return (
    <SlideInContent 
      key={'slide_in_products_menu'} 
      ref={animationObjectRef} 
      className={'container floating_container page_products_menu'}
      useAnimation={useAnimation}
      onHidden={handleSlideOutComplete}
    >
      {renderContent()}
    </SlideInContent>
  );
});

export default withAppContext(ProductsMenu);